import { Pillar, Toasts } from 'components'
import { toggleCookieHubWheel } from 'utility/cookie-hub-wheel'
import { FormHelpers } from 'utility/form-helpers'
import { formatNumber } from 'utility/format-price'
import { TrashIcon, WarningIcon } from '../icons'
import {
  ICartRecycleGradingForm,
  ICartRecycleItem,
  ICartRecycleQuestion,
  ICartRecycleResponse,
  IPriceCalculationResponse,
} from '../interfaces/cart-recycle-interfaces'
import { SideCart } from './side-cart'

const priceRequestParams = {
  GradingForm: {
    Answers: {},
    Extras: [],
    SystemExtras: [],
    GradingDifferenceImages: {},
  },
  ModelId: 0,
  ItemIdentifier: null,
  LocationId: null,
  AgentId: null,
  TradeInChannel: 0,
  TradeInType: 0,
}

const el = {
  triggers: document.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>(
    '.cart-recycle-button'
  ),
  cartRecycle: document.querySelector<HTMLDivElement>('.cart-recycle'),
  sideCart: document.querySelector<HTMLDivElement>('.side-cart'),
  sideExtension: document.querySelector<HTMLDivElement>('.side-extension'),
  overlay: document.querySelector<HTMLDivElement>('.overlay'),
  mobileMenu: document.querySelector<HTMLDivElement>('.mobile-menu'),
  mobileBurger: document.querySelector<HTMLButtonElement>('.header__burger'),
  questionsContainer: document.querySelector<HTMLDivElement>('.cart-recycle__questions'),
  resultsContainers: document.querySelectorAll<HTMLDivElement>('.cart-recycle__results'),
  price: document.querySelector<HTMLSpanElement>('.cart-recycle__price'),
  searchInput: document.querySelector<HTMLInputElement>(
    '.cart-recycle [name="cartRecycleSearch"]'
  ),
  searchSuggestions: document.querySelector<HTMLInputElement>(
    '.cart-recycle .cart-recycle__search  .input__dropdown'
  ),
  submitButton: document.querySelector<HTMLButtonElement>(
    '.cart-recycle__button--submit'
  ),
  form: document.querySelector<HTMLFormElement>('.cart-recycle__form'),
  steps: document.querySelectorAll<HTMLDivElement>('.cart-recycle__step'),
  priceCalculationButton: document.querySelector<HTMLButtonElement>(
    '.cart-recycle__step[data-step="2"] .cart-recycle__button--proceed'
  ),
  backButtons: document.querySelectorAll<HTMLButtonElement>(
    '.cart-recycle__button--back'
  ),
  terms: document.querySelector<HTMLInputElement>('[name="cartRecycleTerms"]'),
}

let timeout

export const CartRecycle = {
  init: () => {
    if (!el.cartRecycle) return
    el.triggers.forEach((target) =>
      target.addEventListener('click', CartRecycle.openMenu)
    )
    el.backButtons.forEach((target) =>
      target.addEventListener('click', CartRecycle.onBackButtonClick)
    )
    if (el.overlay) el.overlay.addEventListener('click', CartRecycle.closeMenu)
    if (el.searchInput) {
      window.addEventListener('click', CartRecycle.onSearchInputOutsideClick)
      el.searchInput.addEventListener('input', CartRecycle.onSearch)
      el.searchInput.addEventListener('click', CartRecycle.onSearchInputClick)
    }
    if (el.form) el.form.addEventListener('submit', CartRecycle.onSubmit)
    if (el.priceCalculationButton)
      el.priceCalculationButton.addEventListener('click', CartRecycle.calculatePrice)
    if (el.terms) el.terms.addEventListener('change', CartRecycle.onTermsChange)
  },

  openMenu: async (e: Event & { target: HTMLAnchorElement | HTMLButtonElement }) => {
    e.preventDefault()

    el.cartRecycle.classList.add('cart-recycle--open')
    document.body.classList.add('h-disable-scroll')
    if (el.overlay) el.overlay.classList.add('overlay--visible')
    if (el.sideCart) el.sideCart.classList.remove('side-cart--open')
    if (el.sideExtension) el.sideExtension.classList.remove('side-extension--open')
    if (el.mobileMenu) el.mobileMenu.classList.remove('mobile-menu--open')
    if (el.mobileBurger) el.mobileBurger.classList.remove('burger--open')
    CartRecycle.autoFocusInput()
    toggleCookieHubWheel({ hide: true })

    const { destination = '' } = e.target.dataset
    CartRecycle.setDestination(destination)
  },

  closeMenu: () => {
    el.cartRecycle.classList.remove('cart-recycle--open')

    document.body.classList.remove('h-disable-scroll')
    if (el.overlay) el.overlay.classList.remove('overlay--visible')
    if (el.searchInput) el.searchInput.value = ''
    toggleCookieHubWheel({ hide: true })
    CartRecycle.reset()
  },

  onSearch: (e: Event & { target: HTMLInputElement }) => {
    if (timeout) window.clearTimeout(timeout)

    if (e.target.value.length < 3) {
      el.searchSuggestions.classList.remove('input__dropdown--visible')
      el.searchSuggestions.innerHTML = ''
      return
    }

    timeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `/umbraco/api/replace/searchmodel?name=${e.target.value}`
        )
        const data: ICartRecycleResponse = await response.json()

        if (!data.Data.length || el.searchInput.value.length < 3) {
          el.searchSuggestions.classList.remove('input__dropdown--visible')
          el.searchSuggestions.innerHTML = ''
          return
        }

        el.searchSuggestions.classList.add('input__dropdown--visible')
        el.searchSuggestions.innerHTML = data.Data.map((suggestion) =>
          CartRecycle.generateSuggestionHtml(suggestion)
        ).join('')

        const suggestions = el.searchSuggestions.querySelectorAll<HTMLButtonElement>(
          '.input__dropdown-item'
        )
        suggestions.forEach((target) =>
          target.addEventListener('click', CartRecycle.onSuggestionClick)
        )
      } catch (_) {
        Toasts.addToast({
          title: 'Villa kom upp!',
          text: 'Ekki tókst að sækja tillögur.',
          icon: WarningIcon(),
        })
      }
    }, 300)
  },

  onSuggestionClick: async (e: Event & { target: HTMLButtonElement }) => {
    const { id = '', img = '', title = '' } = e.target.dataset

    try {
      const response = await fetch(`/umbraco/api/replace/getgradingform?id=${id}`)
      const data: ICartRecycleGradingForm = await response.json()

      // display selected item and proceed button on step 1
      const stepOneResultsContainer = el.cartRecycle.querySelector<HTMLDivElement>(
        '.cart-recycle__step[data-step="1"] .cart-recycle__results'
      )

      if (stepOneResultsContainer)
        stepOneResultsContainer.innerHTML = `
        ${CartRecycle.generateProductHtml({ image: img, title, showDeleteButton: true })}
        ${CartRecycle.generateProceedButtonHtml()}
      `

      // display selected item on step 3
      const stepThreeResultsContainer = el.cartRecycle.querySelector<HTMLDivElement>(
        '.cart-recycle__step[data-step="3"] .cart-recycle__results'
      )
      if (stepThreeResultsContainer)
        stepThreeResultsContainer.innerHTML = CartRecycle.generateProductHtml({
          image: img,
          title,
        })

      // active remove button on selected item
      const removeButton = el.cartRecycle.querySelector<HTMLButtonElement>(
        '.cart-recycle-item__remove'
      )
      if (removeButton) removeButton.addEventListener('click', CartRecycle.reset)

      // activate proceed button on step 1
      const proceedButton = el.cartRecycle.querySelector<HTMLButtonElement>(
        '.cart-recycle__button--proceed[data-step="1"]'
      )
      if (proceedButton)
        proceedButton.addEventListener('click', CartRecycle.onProceedButtonClick)

      // render questions
      el.questionsContainer.innerHTML = data.Questions.map((question) =>
        CartRecycle.generateQuestionHtml(question)
      ).join('')

      // activate pillars in questions container
      const pillars = el.questionsContainer.querySelectorAll<HTMLLabelElement>('.pillar')
      pillars.forEach((target) => Pillar(target))

      // set id and title of product on price calculation button
      if (el.priceCalculationButton) {
        el.priceCalculationButton.setAttribute('data-id', id)
        el.priceCalculationButton.setAttribute('data-title', title)
      }
    } catch (_) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Ekki tókst velja vöru.',
        icon: WarningIcon(),
      })
    }
  },

  calculatePrice: async (e: Event & { target: HTMLButtonElement }) => {
    FormHelpers.toggleButtonState({ isLoading: true, buttons: [e.target] })
    try {
      const { id = '', title = '' } = e.target.dataset
      const checkedInputs =
        el.questionsContainer.querySelectorAll<HTMLInputElement>('input:checked')
      const answers = Array.from(checkedInputs).reduce((prev, el, index) => {
        prev[index] = el.value === 'yes' ? [0] : [1]
        return prev
      }, {})

      const response = await fetch(`/umbraco/api/replace/getprice`, {
        method: 'post',
        body: JSON.stringify({
          ...priceRequestParams,
          ModelId: id,
          GradingForm: {
            ...priceRequestParams.GradingForm,
            Answers: answers,
          },
        }),
        headers: { 'Content-Type': 'application/json' },
      })

      const data: IPriceCalculationResponse = await response.json()

      // add necessary attributes for submit form
      const priceQuoteIdInput = el.form?.querySelector<HTMLInputElement>(
        '[name="priceQuoteId"]'
      )
      if (priceQuoteIdInput) priceQuoteIdInput.value = data.PriceQuoteId
      const titleInput = el.form?.querySelector<HTMLInputElement>('[name="name"]')
      if (titleInput) titleInput.value = title
      const priceInput = el.form?.querySelector<HTMLInputElement>('[name="price"]')
      if (priceInput) priceInput.value = data.OfferedPrice.toString()

      // update price on step 3
      if (el.price)
        el.price.innerText = `${formatNumber(data.OfferedPrice)} ${data.CurrencySymbol}`

      // reset values of terms and submit button
      if (el.terms) el.terms.checked = false
      if (el.submitButton) el.submitButton.disabled = true

      // go to final step
      CartRecycle.showStep(3)
    } catch (_) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Ekki tókst reikna verð á vöru.',
        icon: WarningIcon(),
      })
    }
    FormHelpers.toggleButtonState({ isLoading: false, buttons: [e.target] })
  },

  onSubmit: async (e: Event & { target: HTMLFormElement }) => {
    e.preventDefault()

    if (!el.terms || !el.terms.checked) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Þú þarft að samþykkja skilmálana til þess að bæta vöru í uppítöku.',
        icon: WarningIcon(),
      })
      return
    }

    FormHelpers.submitForm({
      form: e.target,
      buttons: [el.submitButton],
      onSuccess: () => {
        CartRecycle.shallowCloseMenu()
        CartRecycle.reset()
        SideCart.openMenu()
      },
      onError: (message = '') => {
        Toasts.addToast({
          title: 'Villa kom upp!',
          text: message || 'Ekki tókst að bæta vöru í uppítöku.',
          icon: WarningIcon(),
        })
      },
    })
  },

  onSearchInputOutsideClick: (e: Event & { target: Element }) => {
    const isClickInside = el.searchInput.contains(e.target)
    if (isClickInside) return
    el.searchSuggestions.classList.remove('input__dropdown--visible')
  },

  onSearchInputClick: (e: Event & { target: HTMLInputElement }) => {
    if (e.target.value.length < 3) return
    if (!el.searchSuggestions.querySelector('.input__dropdown-item')) return
    el.searchSuggestions.classList.add('input__dropdown--visible')
  },

  showStep: (step = 1) => {
    const currentStep = el.cartRecycle.querySelector<HTMLDivElement>(
      `.cart-recycle__step[data-step="${step}"]`
    )
    if (!currentStep) return
    el.steps.forEach((target) => target.classList.add('hide'))
    currentStep.classList.remove('hide')
    el.cartRecycle.scrollTo(0, 0)
  },

  reset: () => {
    if (el.resultsContainers.length)
      el.resultsContainers.forEach((target) => (target.innerHTML = ''))
    if (el.questionsContainer) el.questionsContainer.innerHTML = ''
    if (el.price) el.price.innerHTML = ''
    if (el.terms) el.terms.checked = false
    if (el.submitButton) el.submitButton.disabled = true
    if (el.searchInput) el.searchInput.value = ''
    CartRecycle.showStep(1)
  },

  onProceedButtonClick: (e: Event & { target: HTMLButtonElement }) => {
    try {
      const { step = '' } = e.target.dataset
      CartRecycle.showStep(parseInt(step) + 1)
    } catch (_) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Ekki tókst að fara á næsta skref',
        icon: WarningIcon(),
      })
    }
  },

  onBackButtonClick: (e: Event & { target: HTMLButtonElement }) => {
    const { step = '' } = e.target.dataset
    try {
      const newStep = parseInt(step) - 1
      if (newStep <= 0) {
        const { destination = '' } = e.target.dataset
        if (destination === 'side-cart') {
          CartRecycle.shallowCloseMenu()
          CartRecycle.setDestination('')
          SideCart.openMenu()
          return
        }

        CartRecycle.closeMenu()
        return
      }
      CartRecycle.showStep(newStep)
    } catch (_) {
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Ekki tókst að fara á fyrra skref',
        icon: WarningIcon(),
      })
    }
  },

  setDestination: (destination = '') => {
    const stepOneBackButton = el.cartRecycle.querySelector<HTMLButtonElement>(
      '.cart-recycle__button--back[data-step="1"]'
    )
    if (stepOneBackButton) stepOneBackButton.setAttribute('data-destination', destination)
  },

  onTermsChange: (e: Event & { target: HTMLInputElement }) => {
    if (!el.submitButton) return

    if (!e.target.checked) {
      el.submitButton.disabled = true
      return
    }

    el.submitButton.disabled = false
  },

  shallowCloseMenu: () => {
    if (el.cartRecycle) el.cartRecycle.classList.remove('cart-recycle--open')
  },

  generateProductHtml: ({ image = '', title = '', showDeleteButton = false }) => `
    <div class="cart-recycle-item ${!image.length ? 'cart-recycle-item--without-image' : ''
    }">
      ${image.length
      ? `
        <div class="cart-recycle-item__image">
          <img src="${image}" alt="${title}">
        </div>
      `
      : ''
    }
      <h4 class="heading--small text--bold text--black">${title
      .split('&quot;')
      .join('"')}</h4>
      ${showDeleteButton
      ? `
        <button type="submit" class="action-button action-button--opaque cart-recycle-item__remove">
          ${TrashIcon()}
        </button>
      `
      : ''
    }
    </div>
  `,

  generateSuggestionHtml: (product: ICartRecycleItem) => `
    <button 
      data-id="${product.Id}"
      data-title="${product.ProductName.split('"').join('&quot;')}"
      data-img="${product.PictureUrl || ''}"
      class="input__dropdown-item"
    >
      ${product.ProductName}
    </button>
  `,

  generateProceedButtonHtml: () => `
    <button type="button" class="button cart-recycle__button cart-recycle__button--proceed" data-step="1">
      <span class="button__text">Halda áfram</span>
    </button>
  `,

  generateQuestionHtml: (question: ICartRecycleQuestion) => {
    let text = question.Name

    const dictionary = el.cartRecycle.querySelector<HTMLDivElement>(
      '.cart-recycle__dictionary'
    )
    if (dictionary) {
      text =
        dictionary.getAttribute(
          `data-${question.Label.split(' ').join('').toLowerCase()}`
        ) || question.Name
    }

    return `
      <li class="cart-recycle__question">
        <div class="polar-question">
          <p class="text--base text--gray">${text}</p>
          <div class="polar-question__options pillar-group">
            <label class="pillar pillar--radio  pillar--selected">
              <input type="radio" class="pillar__input" name="${question.Label.toLowerCase()}" value="yes" checked>
              <span class="pillar__text">Já</span>
            </label>
            <label class="pillar pillar--radio">
              <input type="radio" class="pillar__input" name="${question.Label.toLowerCase()}" value="no">
              <span class="pillar__text">Nei</span>
            </label>
          </div>
        </div>
      </li>
    `
  },

  autoFocusInput: () => {
    const { innerWidth } = window
    if (innerWidth < 576) return
    if (el.searchInput) el.searchInput.focus()
  },
}
